import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { headPivot, HUMAN_HEAD_ORIGIN, mix } from '../../../content/characters/rigs/human.js';
import type { CharacterRig } from './character-rig.js';

/** Warm ivory the feathers lean towards; the hair colour tints them. */
const DOWN = 0xefe8da;
const HALO = 0xf1d38a;
/** How far the halo leans back from level, in radians. */
const TILT = .8;
/** How far the wings' springs follow the hair's: the hair swings at most `HAIR_SWAY_REACH` (.06), the wings by this many radians per unit of it. */
const FOLLOW = 2.2;

/**
 * One feather: a tapered card along -y from its quill to its tip, with a raised shaft, curling back (-z) towards the tip. Vertex colours
 * darken the quill end and the vane edges.
 */
function feather(length: number, width: number, curl: number, tone: number): THREE.BufferGeometry {
  const rows = 8, positions: number[] = [], colors: number[] = [], indices: number[] = [];
  for (let row = 0; row <= rows; row++) {
    const t = row / rows, half = width * .5 * (t < .3 ? .45 + .55 * t / .3 : 1 - ((t - .3) / .7) ** 3), y = -t * length, z = -curl * t * t;
    const shade = tone * (.5 + .5 * t ** .6);
    for (const across of [-1, 0, 1]) {
      positions.push(across * half, y, z + (across ? -.002 : .0015));
      const value = Math.min(1, shade * (across ? .7 : 1.08));
      colors.push(value, value, value);
    }
    if (row < rows) for (const column of [0, 1]) {
      const a = row * 3 + column, b = a + 1, c = a + 3, d = a + 4;
      indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices); geometry.computeVertexNormals();
  return geometry;
}

/** A feather placed with its quill at `base` and its tip at `tip`, its vane lying across the back. */
function placed(base: THREE.Vector3, tip: THREE.Vector3, width: number, curl: number, tone: number) {
  const along = tip.clone().sub(base), length = along.length(), down = along.normalize();
  const across = new THREE.Vector3(0, 0, 1).cross(down).normalize(), out = down.clone().cross(across).normalize();
  // Local -y runs to the tip, x across the vane and z out of the back.
  const basis = new THREE.Matrix4().makeBasis(across, down.clone().negate(), out.negate()).setPosition(base);
  return feather(length, width, curl, tone).applyMatrix4(basis);
}

/**
 * A folded wing on the shoulder blade of `side` (1 or -1), in the torso joint's space: the leading edge rises from the blade to the
 * wrist above the shoulder, the primaries fall from the wrist past the hips and cross towards the spine, and the secondaries and two
 * rows of coverts layer over them. Sizes are in rig units (a 2-unit reference figure).
 */
export function foldedWingGeometry(side: number): THREE.BufferGeometry {
  const root = new THREE.Vector3(side * .06, 0, 0), wrist = new THREE.Vector3(side * .25, .3, -.1);
  const along = (u: number) => root.clone().lerp(wrist, u), parts: THREE.BufferGeometry[] = [];
  const noise = (i: number) => .9 + .1 * Math.sin(i * 12.9898 + side * 3.1);
  const row = (count: number, from: number, to: number, tip: (u: number) => THREE.Vector3, width: number, curl: number, lift: number, tone: number) => {
    for (let i = 0; i < count; i++) {
      const k = count === 1 ? 0 : i / (count - 1), u = from + (to - from) * k, base = along(Math.min(u, 1));
      if (u > 1) base.add(new THREE.Vector3(side * .02, -.06, -.01).multiplyScalar((u - 1) / .1));
      base.z -= lift; const end = tip(k); end.z -= lift;
      parts.push(placed(base, end, width, curl, tone * noise(i + count)));
    }
  };
  // Primaries: the longest, from the wrist down to the calves, crossing in towards the spine.
  row(10, .62, 1.1, k => new THREE.Vector3(side * (.2 - .15 * k), -.66 - .4 * k, -.24 - .06 * k), .1, .06, 0, .92);
  // Secondaries hang from the forearm to the hips, fanned across the back.
  row(13, .02, .7, k => new THREE.Vector3(side * (.05 + .17 * k), -.34 - .3 * k, -.2 - .04 * k), .11, .05, .014, .96);
  // Greater and lesser coverts shingle the top of the wing.
  row(13, 0, 1, k => along(k).add(new THREE.Vector3(side * .015, -.3 + .04 * k, -.05)), .095, .03, .028, 1);
  row(12, 0, 1, k => along(k).add(new THREE.Vector3(side * .008, -.17, -.035)), .085, .02, .04, 1);
  row(11, 0, 1, k => along(k).add(new THREE.Vector3(side * .004, -.08, -.02)), .07, .01, .05, 1);
  // The leading edge: the wing's arm under its smallest feathers.
  const arm = new THREE.CapsuleGeometry(.03, root.distanceTo(wrist), 3, 8);
  arm.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), wrist.clone().sub(root).normalize()));
  arm.translate((root.x + wrist.x) / 2, (root.y + wrist.y) / 2, (root.z + wrist.z) / 2 - .02);
  arm.deleteAttribute('uv'); arm.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(arm.getAttribute('position').count * 3).fill(.96), 3));
  parts.push(arm);
  const merged = mergeGeometries(parts, false)!;
  for (const part of parts) part.dispose();
  return merged;
}

/**
 * A Celestial's wings and halo, in-world rather than effects: feathered wings folded on the back, carried by the torso and swung by the
 * same springs that swing the hair, and a ring of soft light above the head so it reads from the game camera. Presentation only.
 */
export function addCelestialRaiment(rig: CharacterRig, hair: number) {
  const p = rig.proportions, torso = rig.torso, head = rig.head;
  const feathers = new THREE.MeshStandardMaterial({ color: mix(hair, DOWN, .86), roughness: .86, side: THREE.DoubleSide, vertexColors: true });
  feathers.name = 'feathers';
  // Across the shoulder blades, just off the back and below the top of the shoulders.
  const blade = p.shoulderY - p.torsoY - .07, width = p.shoulderHalfWidth / .2, wings: THREE.Object3D[] = [];
  for (const side of [-1, 1]) {
    const pivot = new THREE.Group(); pivot.name = side < 0 ? 'wing-right' : 'wing-left';
    pivot.position.set(side * .03, blade, -.125); pivot.scale.set(Math.max(.85, Math.min(1.25, width)), 1, 1);
    const wing = new THREE.Mesh(foldedWingGeometry(side), feathers); wing.name = 'celestial-wing'; wing.castShadow = true; wing.receiveShadow = true;
    pivot.add(wing); torso.add(pivot); wings.push(pivot);
  }
  const top = p.skull.cranium.y + p.skull.cranium.h, pivotAt = headPivot(p);
  const halo = new THREE.Group(); halo.name = 'halo';
  halo.position.set(0, p.headY + (top + .05 - HUMAN_HEAD_ORIGIN) * p.headScale - pivotAt.y, p.headZ - .07 * p.headScale - pivotAt.z);
  // Leaning back towards the nape rather than level with the ground.
  halo.rotation.x = Math.PI / 2 - TILT;
  // Light rather than metal: a faint core inside two widening haloes of glow, so the ring has no hard edge.
  const light = (tube: number, opacity: number) => new THREE.Mesh(new THREE.TorusGeometry(.1, tube, 8, 56),
    new THREE.MeshBasicMaterial({ color: HALO, transparent: true, opacity, blending: THREE.AdditiveBlending, depthWrite: false }));
  const ring = light(.004, .38), glow = light(.014, .16), haze = light(.032, .06);
  ring.name = 'halo-ring'; glow.name = 'halo-glow'; haze.name = 'halo-haze';
  for (const part of [ring, glow, haze]) part.renderOrder = 2;
  halo.add(ring, glow, haze); head.add(halo);
  const base = halo.position.y;
  // The wings breathe and follow the body's swing; the halo bobs. Driven from the figure's own draw, so a figure off screen costs nothing.
  ring.onBeforeRender = () => {
    const time = performance.now() / 1000, sway = rig.hair.state;
    halo.position.y = base + Math.sin(time * 1.3) * .006;
    for (const [i, pivot] of wings.entries()) {
      const side = i ? 1 : -1, breath = Math.sin(time * 1.1 + i * .4) * .025;
      pivot.rotation.set(-sway.back * FOLLOW - breath * .5, side * (breath + sway.side * FOLLOW * .5), side * breath * .4);
    }
  };
  return { wings, halo };
}

/** Celestial eyes shine faintly in their own colour. */
export function shiningEyes(root: THREE.Object3D, strength = .55) {
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh) || Array.isArray(object.material) || object.material.name !== 'eye') return;
    const material = (object.material as THREE.MeshStandardMaterial).clone();
    material.emissive = material.color.clone().multiplyScalar(strength); object.material = material;
  });
}
